import axios from "axios";
import { supabase } from "../config/supabaseClient";
import { assertBackendHelperEnv } from "./backendHelperApi";
import { stripInternalPayloadFields } from "./planOptimizerService";
import { isSelectionFullyCompliant } from "./planOptimizerCompliance";

export const PLAN_OPTIMIZER_MAX_RETRIES = Number(
  process.env.REACT_APP_PLAN_OPTIMIZER_MAX_RETRIES || 5,
);

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

async function postPlanOptimizerEdge(functionName, companyId, body) {
  const { baseUrl, apiKey } = assertBackendHelperEnv();
  const url = `${baseUrl}/${functionName}/${companyId}`;
  const res = await axios.post(url, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
  });
  const data = res.data;
  if (!data?.success) {
    throw new Error(data?.error || data?.message || `${functionName} failed`);
  }
  return data.data;
}

export async function synthesizePlanOptimizerFunctions({
  companyId,
  failedLeg,
  partialBodies,
  setaFieldNames,
}) {
  return postPlanOptimizerEdge("planOptimizerSynthesize", companyId, {
    companyId,
    failedLeg,
    partialBodies,
    setaFieldNames,
  });
}

export async function executePlanOptimizerEngine({
  companyId,
  expandedPayload,
  allocatorFunctionBody,
  setaMapperFunctionBody,
  sliderMeta,
  aiAttempts,
  aiSkipped,
  setaFieldNames,
  yearId,
  passthroughOnly,
}) {
  return postPlanOptimizerEdge("planOptimizerExecute", companyId, {
    companyId,
    expandedPayload,
    allocatorFunctionBody,
    setaMapperFunctionBody,
    sliderMeta,
    aiAttempts,
    aiSkipped,
    setaFieldNames,
    yearId,
    passthroughOnly,
  });
}

/**
 * Full v2 Generate orchestrator.
 * Compliance is checked on the frontend (no extra API):
 * - Already within all budgets → one execute (passthrough, no synthesize)
 * - Needs cuts → synthesize → execute (same as before)
 */
export async function runPlanOptimizerV2Generate({
  companyId,
  expandedPayload,
  sliderMeta,
  setaFieldNames,
  yearId,
}) {
  // FE-only compliance: skip synthesize when selection already fits all budgets.
  // Server trusts passthroughOnly and builds an identity plan (no re-check).
  if (isSelectionFullyCompliant(expandedPayload)) {
    try {
      const passthrough = await executePlanOptimizerEngine({
        companyId,
        expandedPayload,
        sliderMeta,
        aiAttempts: 0,
        aiSkipped: true,
        setaFieldNames,
        yearId,
        passthroughOnly: true,
      });
      if (passthrough.status === "ok" && passthrough.artifact) {
        return passthrough.artifact;
      }
    } catch {
      // Hard execute failure — fall through to synthesize + enforce.
    }
  }

  let allocatorFunctionBody;
  let setaMapperFunctionBody;
  let aiAttempts = 0;
  let partialBodies = {};
  for (let attempt = 1; attempt <= PLAN_OPTIMIZER_MAX_RETRIES; attempt++) {
    aiAttempts = attempt;
    const synth = await synthesizePlanOptimizerFunctions({
      companyId,
      failedLeg: attempt > 1 ? partialBodies.failedLeg : undefined,
      partialBodies: {
        allocatorFunctionBody: partialBodies.allocatorFunctionBody,
        setaMapperFunctionBody: partialBodies.setaMapperFunctionBody,
      },
      setaFieldNames,
    });

    if (synth.status === "ok") {
      allocatorFunctionBody = synth.allocatorFunctionBody;
      setaMapperFunctionBody = synth.setaMapperFunctionBody;
      break;
    }

    if (synth.retryable) {
      if (synth.partial?.allocatorFunctionBody) {
        partialBodies.allocatorFunctionBody = synth.partial.allocatorFunctionBody;
      }
      if (synth.partial?.setaMapperFunctionBody) {
        partialBodies.setaMapperFunctionBody = synth.partial.setaMapperFunctionBody;
      }
      partialBodies.failedLeg = synth.failedLeg;
      if (attempt < PLAN_OPTIMIZER_MAX_RETRIES) {
        await delay(800 * attempt);
        continue;
      }
    }

    throw new Error(
      synth.reason || "Could not generate valid optimizer functions. Please try again.",
    );
  }

  if (!allocatorFunctionBody || !setaMapperFunctionBody) {
    throw new Error("Could not generate valid optimizer functions. Please try again.");
  }

  for (let execAttempt = 0; execAttempt < PLAN_OPTIMIZER_MAX_RETRIES; execAttempt++) {
    const exec = await executePlanOptimizerEngine({
      companyId,
      expandedPayload,
      allocatorFunctionBody,
      setaMapperFunctionBody,
      sliderMeta,
      aiAttempts,
      aiSkipped: false,
      setaFieldNames,
      yearId,
    });

    if (exec.status === "ok" && exec.artifact) {
      return exec.artifact;
    }

    if (exec.retryable && exec.failedLeg) {
      if (exec.failedLeg === "allocator" || exec.failedLeg === "both") {
        allocatorFunctionBody = undefined;
      }
      if (exec.failedLeg === "seta" || exec.failedLeg === "both") {
        setaMapperFunctionBody = undefined;
      }
      const synth = await synthesizePlanOptimizerFunctions({
        companyId,
        failedLeg: exec.failedLeg,
        partialBodies: {
          allocatorFunctionBody,
          setaMapperFunctionBody,
        },
        setaFieldNames,
      });
      if (synth.status === "ok") {
        allocatorFunctionBody = synth.allocatorFunctionBody;
        setaMapperFunctionBody = synth.setaMapperFunctionBody;
        continue;
      }
      throw new Error(
        synth.reason || exec.reason || "Plan execution failed validation.",
      );
    }

    throw new Error(exec.reason || "Plan execution failed.");
  }

  throw new Error("Plan execution failed after retries.");
}

const ROW_CHUNK_SIZE = 500;

export async function savePlanOptimizerV2Artifact({
  companyId,
  yearId,
  createdBy,
  compactPayload,
  artifact,
}) {
  const responseSummary = {
    allocation_summary: artifact.allocation_summary,
    generated_plan: artifact.generated_plan,
    engine_meta: artifact.engine_meta,
  };

  const { data: savedPlan, error: saveError } = await supabase.rpc("insert_plan_optimizer_plan", {
    p_company_id: companyId,
    p_year_id: yearId ?? null,
    p_created_by: createdBy ?? null,
    p_input_data: stripInternalPayloadFields(compactPayload),
    p_response_summary: responseSummary,
    p_completed_at: new Date().toISOString(),
  });

  if (saveError) {
    throw new Error(saveError.message);
  }

  const planRow = Array.isArray(savedPlan) ? savedPlan[0] : savedPlan;
  const planId = planRow?.id;
  if (!planId) {
    throw new Error("Save failed: no plan id returned");
  }

  const setaRows = artifact.seta_rows || [];
  try {
    for (let i = 0; i < setaRows.length; i += ROW_CHUNK_SIZE) {
      const chunk = setaRows.slice(i, i + ROW_CHUNK_SIZE).map((data) => ({
        plan_optimizer_id: planId,
        data,
      }));
      const { error: rowError } = await supabase.from("plan_optimizer_rows").insert(chunk);
      if (rowError) {
        throw new Error(rowError.message);
      }
    }

    const { error: snapError } = await supabase.rpc("capture_plan_optimizer_scope_snapshot", {
      p_plan_optimizer_id: planId,
    });
    if (snapError) {
      console.error("capture_plan_optimizer_scope_snapshot:", snapError);
      throw new Error(snapError.message);
    }
  } catch (err) {
    await supabase.from("plan_optimizer_rows").delete().eq("plan_optimizer_id", planId);
    await supabase.from("plan_optimizer").delete().eq("id", planId);
    throw err;
  }

  return planRow;
}

export function isPlanTransformationComplete(plan) {
  return Boolean(plan?.scope_captured_at);
}

export function listSetaFieldNamesFromTemplate(template) {
  if (!template) return [];
  const fields =
    template?.sheets?.[0]?.fields || template?.fields || (Array.isArray(template) ? template : []);
  return (fields || [])
    .map((f) => f?.fieldName)
    .filter(Boolean);
}
